// Lens — a non-destructive view filter for the location editor. It does two
// independent things:
//   1. hides/shows whole sections of every location card at once (e.g. "only
//      Task", "only Comments");
//   2. narrows the set of points itself down to those carrying one of the
//      picked tags (OR logic) and addressed to a given recipient — hidden
//      points drop out of the list and go pale and unclickable on the map.
// The lens object maps a section key -> boolean; a key that is absent or true
// means visible, an explicit false hides that section. The point filter lives
// under namespaced keys (see LENS_TAGS_KEY / LENS_ASSIGNEE_KEY). An empty
// object therefore means "show everything" (the default).
//
// Keys here are the single source of truth shared by the control (QuestSelector)
// and the consumer (LocationsList) — keep them in sync with the gated blocks.
export const LENS_SECTIONS = [
  { key: 'meta', label: 'Meta / Tags' },   // UUID, Coordinates, Type, Paid, Tags
  { key: 'comments', label: 'Comments' },
  { key: 'description', label: 'Description' },
  { key: 'task', label: 'Task' },
  { key: 'answers', label: 'Possible Answers' },
  { key: 'hint', label: 'Hint' },
  { key: 'after', label: 'After Message' },
  { key: 'branching', label: 'Branching' },
];

// Is a section visible under the current lens?
export const lensShows = (lens, key) => !lens || lens[key] !== false;

// Any section hidden?
export const lensSectionsActive = (lens) =>
  !!lens && LENS_SECTIONS.some((s) => lens[s.key] === false);

// The standing editorial-cycle tags: quality verdicts and hand-off states. They
// are offered everywhere unconditionally — as quick-add buttons in the tag
// editor and as chips in the Lens — whether or not any point carries them yet,
// because "nobody has marked this quest ready for voice-over" is exactly the
// answer someone reaching for that filter is after. Anything else a quest uses
// is a custom tag and shows up only once it is actually in use.
export const PREDEFINED_TAGS = [
  'Good',
  'Acceptable',
  'Achtung',
  '⏳ Waiting for review',
  '🎙️ Ready for voiceover',
];

// Reserved slot inside the lens object holding the point-level tag filter: an
// array of tag names, OR logic. The `__` prefix keeps it clear of every section
// key above, so old persisted lenses stay readable as-is.
export const LENS_TAGS_KEY = '__tags';

// The picked tags, always as an array.
export const lensTags = (lens) => {
  const picked = lens && lens[LENS_TAGS_KEY];
  return Array.isArray(picked) ? picked : [];
};

// Return a copy of the lens with a different tag selection.
export const lensWithTags = (lens, tags) => ({ ...(lens || {}), [LENS_TAGS_KEY]: tags || [] });

// Add/remove one tag from the selection.
export const lensToggleTag = (lens, tag) => {
  const picked = lensTags(lens);
  return lensWithTags(lens, picked.includes(tag) ? picked.filter((t) => t !== tag) : [...picked, tag]);
};

// Second slot of the point filter: whom the tag is addressed to. Absent/null =
// anyone. LENS_ASSIGNEE_ME stands for "the signed-in user" so the choice keeps
// meaning after another person logs in on the same browser.
export const LENS_ASSIGNEE_KEY = '__assignee';
export const LENS_ASSIGNEE_ME = '__me__';

// The pseudo-recipient meaning "everybody" — mirrors the tag editor and Tags
// Summary, where a tag can be addressed to All instead of named people.
export const ALL_RECIPIENTS = 'All';

export const lensAssignee = (lens) => (lens && lens[LENS_ASSIGNEE_KEY]) || null;

export const lensWithAssignee = (lens, assignee) => ({
  ...(lens || {}),
  [LENS_ASSIGNEE_KEY]: assignee || null,
});

// Does this point pass the tag filter? Nothing picked = every point passes.
export const lensMatchesTags = (lens, location) => {
  const picked = lensTags(lens);
  if (picked.length === 0) return true;
  const own = (location && location.tags) || [];
  return picked.some((t) => own.includes(t));
};

// Does this point pass the whole point filter — tags AND recipient?
//
// The recipient test is scoped to the tags the filter is about: with "waiting
// for review" picked, asking for Masha keeps only the points where *that* tag
// is addressed to her, not the ones where she merely owns some other tag. With
// no tag picked the scope widens to every tag on the point.
//
// `tagAssignees` is one point's { [tag]: [recipient, ...] } map (see
// tagAssigneesByLocation in App.js, keyed by location uuid).
export const lensMatchesPoint = (lens, location, tagAssignees = {}, currentUsername = null) => {
  if (!lensMatchesTags(lens, location)) return false;
  const who = lensAssignee(lens);
  if (!who) return true;
  const target = who === LENS_ASSIGNEE_ME ? currentUsername : who;
  if (!target) return false;
  const picked = lensTags(lens);
  const own = (location && location.tags) || [];
  const scope = picked.length > 0 ? own.filter((t) => picked.includes(t)) : own;
  return scope.some((t) => {
    const list = (tagAssignees && tagAssignees[t]) || [];
    return list.includes(target) || list.includes(ALL_RECIPIENTS);
  });
};

// Is the point filter (tags and/or recipient) narrowing anything?
export const lensPointsActive = (lens) => lensTags(lens).length > 0 || !!lensAssignee(lens);

// Anything filtered at all? (drives the "filter active" indicator on the button).
export const lensIsActive = (lens) => lensSectionsActive(lens) || lensPointsActive(lens);

// How many sections are currently visible.
export const lensVisibleCount = (lens) =>
  LENS_SECTIONS.filter((s) => lensShows(lens, s.key)).length;

// Isolate a single section: only `key` visible, everything else hidden.
export const lensOnly = (key) =>
  Object.fromEntries(LENS_SECTIONS.map((s) => [s.key, s.key === key]));
